import React, { useEffect, useState, useRef } from "react";
import AdminAssets from "./AdminAssets";
import AdminSidebar from "./AdminSidebar";
import axios from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const AdminBlogs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);

  // <‑‑ NEW: track if we’re editing (holds the _id of the blog being edited) –‑>
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    image: "",
    content: "",
    category: "",
    date: "",
    author: "",
  });

  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  /* ───────────────  QUILL: custom image upload + modules  ──────────────── */

  const handleQuillImage = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const data = new FormData();
      data.append("image", file);

      try {
        const { data: res } = await axios.post(
          `${process.env.REACT_APP_API_BASE}/blogs/upload`,
          data
        );

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        // insert the uploaded image HTML at the cursor
        editor.clipboard.dangerouslyPasteHTML(
          range.index,
          `<img src="${res.url}" style="max-width:100%;width:400px;height:auto;" />`
        );
      } catch (err) {
        console.error(err);
        alert("Image upload failed");
      }
    };
  };

  const quillModules = {
    toolbar: {
      container: [
        [{ font: [] }, { size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ script: "sub" }, { script: "super" }],
        [{ color: [] }, { background: [] }],
        [
          { align: "" }, // left
          { align: "center" }, // center
          { align: "right" }, // right
          { align: "justify" }, // justify
        ],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: {
        image: handleQuillImage,
        video: () => {
          const url = prompt("Enter YouTube or video URL");
          if (url && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true);
            editor.insertEmbed(range.index, "video", url, "user");
          }
        },
      },
    },
  };

  // useEffect(() => {
  //   axios
  //     .get(`${process.env.REACT_APP_API_BASE}/admin/session`, { withCredentials: true })
  //     .then(() => {
  //       setLoading(false);
  //       fetchBlogs();
  //     })
  //     .catch(() => navigate('/admin'));
  // }, [navigate]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE}/admin/session`)
      .then((res) => {
        if (!res.data.loggedIn) {
          navigate("/admin");
        } else {
          setLoading(false);
          fetchBlogs();
        }
      })
      .catch(() => {
        navigate("/admin");
      });
  }, [navigate]);

  const fetchBlogs = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_BASE}/blogs`);
    console.log("Fetched blogs:", res.data); 
    setBlogs(res.data); // inspect this data
  } catch (err) {
    console.error("Error fetching blogs:", err);
  }
};

  /* ──────────────────────────────  CRUD HELPERS  ───────────────────────────── */

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE}/blogs/${id}`);
      setBlogs((prev) => prev.filter((b) => b._id !== id));
    } catch (err) {
      console.error("Failed to delete blog:", err);
    }
  };

  // <‑‑ NEW: open the modal in “edit” mode –‑>
  const handleEditClick = (blog) => {
    setEditingId(blog._id); // remember which blog
    setFormData({
      title: blog.title,
      image: "", // leave empty (keeps old unless user chooses new)
      content: blog.content,
      category: blog.category || "",
      date: new Date(blog.date).toISOString().split("T")[0], // yyyy‑mm‑dd for <input type="date">
      author: blog.author,
    });
    window.$("#myModal").modal("show");
  };

  /* ──────────────────────────────  FORM HANDLERS  ─────────────────────────── */

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  /*  image upload handler inside Quill omitted (kept identical to your original)  */

  /* ──────────────────────────────  SUBMIT  ─────────────────────────────────── */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = new FormData();
    const formattedDate = new Date(formData.date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    form.append("title", formData.title);
    form.append("content", formData.content);
    form.append("category", formData.category);
    form.append("date", formattedDate);
    form.append("author", formData.author);
    if (formData.image) form.append("image", formData.image);

    const isEdit = Boolean(editingId);
    const url = isEdit
      ? `${process.env.REACT_APP_API_BASE}/blogs/${editingId}`
      : `${process.env.REACT_APP_API_BASE}/blogs`;

    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: form,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Unknown error");

      alert(isEdit ? "Blog updated!" : "Blog submitted!");
      resetForm();
      window.$("#myModal").modal("hide");
      fetchBlogs();
    } catch (err) {
      console.error(err);
      alert(`Error ${isEdit ? "updating" : "submitting"} blog: ${err.message}`);
    }
  };

  const resetForm = () => {
    setEditingId(null); // leave “edit” mode
    setFormData({
      title: "",
      image: "",
      content: "",
      keywords: "",
      date: "",
      author: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (loading) return null;

  /* ──────────────────────────────  RENDER  ────────────────────────────────── */

  return (
    <div>
      <AdminAssets />
      <AdminSidebar />

      <section className="content">
        <div className="container-fluid">
          {/*  Add‑/Edit‑trigger  */}
          <button
            className="btn btn-primary"
            data-toggle="modal"
            data-target="#myModal"
            onClick={resetForm} /* clears & switches to “add” */
          >
            Add Blog
          </button>
          <br />
          <br />

          {/*  ADD / EDIT  modal  */}
          <div id="myModal" className="modal fade" role="dialog">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <h5 className="mb-3 fw-bold">
                      {editingId ? "Edit Blog" : "Add Blog"}
                    </h5>

                    {/*  Title  */}
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/*  Image  */}
                    <div className="mb-3">
                      <label className="form-label">
                        {editingId ? "Change Image (optional)" : "Image"}
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="image"
                        onChange={handleImageChange}
                        ref={fileInputRef}
                      />
                    </div>

                    {/*  Content (Quill)  */}
                    <div className="mb-3">
                      <label className="form-label">Content</label>
                      <ReactQuill
                        ref={quillRef}
                        modules={quillModules}
                        value={formData.content}
                        onChange={(html) =>
                          setFormData((prev) => ({ ...prev, content: html }))
                        }
                        theme="snow"
                        style={{ height: "280px", marginBottom: "3rem" }}
                      />
                    </div>
                    <br />
                    <br />
                    <br />
                    <br />
                    {/*  Categories  */}
                    <div className="mb-3">
                      <label className="form-label">Category</label>
                      <select
                        className="form-control"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>
                        <option value="Technology">Technology</option>
                        <option value="AI & ML">AI & ML</option>
                        <option value="Education">Education</option>
                        <option value="Business">Business</option>
                        <option value="Health">Health</option>
                        <option value="Lifestyle">Lifestyle</option>
                        <option value="News">News</option>
                        <option value="Sports">Sports</option>
                        {/* Add more categories as needed */}
                      </select>
                    </div>

                    {/*  Date & Author  */}
                    <div className="row">
                      <div className="col-sm-6 mb-3">
                        <label className="form-label">Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="date"
                          value={formData.date}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-sm-6 mb-3">
                        <label className="form-label">Author</label>
                        <input
                          type="text"
                          className="form-control"
                          name="author"
                          value={formData.author}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="submit" className="btn btn-success">
                      {editingId ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      data-dismiss="modal"
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/*  BLOG LIST  */}
          <div className="row clearfix">
            <div className="col-12">
              <div className="card">
                <div className="header">
                  <h2 className="fw-bold text-start">Blogs</h2>
                </div>
                <div className="body">
                  {blogs.length === 0 ? (
                    <p>No blogs found.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped table-hover">
                        <thead>
                          <tr>
                            <th style={{ width: 140 }}>Actions</th>
                            <th>Title</th>
                            <th>Category</th> 
                            <th>Author</th>
                            <th>Image</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blogs.map((b) => (
                            <tr key={b._id}>
                              <td className="text-nowrap">
                                {/*  EDIT  */}
                                <button
                                  className="btn btn-sm btn-warning me-2"
                                  onClick={() => handleEditClick(b)}
                                >
                                  Edit
                                </button>
                                {/*  DELETE  */}
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(b._id)}
                                >
                                  Delete
                                </button>
                              </td>

                              <td>{b.title}</td>
                              <td>{b.category}</td>
                              <td>{b.author}</td>
                              <td>
                                {b.image && (
                                  <img
                                    src={`${process.env.REACT_APP_API_BASE}/uploads/blog/${b.image}`}
                                    alt="Blog thumbnail"
                                    style={{ width: 80, objectFit: "cover" }}
                                  />
                                )}
                              </td>
                              <td>{b.date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminBlogs;
